import { Info } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { SessionTiming } from "@/lib/api";
import { formatSeconds } from "@/lib/format";
import { cn } from "@/lib/utils";

const THINK = "bg-primary";
const SERVER = "bg-emerald-500";
const OTHER = "bg-muted-foreground/30";

function ms(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(digits)} ms`;
}

/**
 * Where a run's wall clock went, as a narrow card.
 *
 * One bar, three colours -- the student's code, our machine, everything else -- then the
 * per-step figures for the student's code with the gateway's measurement first and the
 * SDK's report second. Explanations live in tooltips so the card stays a column of
 * numbers; the ranking note is the one sentence that must always be visible.
 */
export function SessionTimingCard({
  timing,
  className,
}: {
  timing: SessionTiming;
  className?: string;
}) {
  const wall = timing.wall_ms;
  const other =
    wall === null ? null : Math.max(0, wall - timing.think_ms - timing.server_ms);
  const share = (part: number) =>
    wall === null || wall <= 0 ? 0 : Math.min(100, (part / wall) * 100);
  const pct = (part: number | null) =>
    part === null || wall === null || wall <= 0 ? null : Math.round((part / wall) * 100);

  const reportedDiffers =
    timing.think_ms > 0 &&
    Math.abs(timing.reported_ms - timing.think_ms) / timing.think_ms > 0.25;

  return (
    <div className={cn("rounded-lg border p-4 text-sm", className)}>
      <div className="flex items-baseline justify-between">
        <span className="font-medium">Wall time</span>
        <span className="font-mono">{wall === null ? "—" : formatSeconds(wall / 1000)}</span>
      </div>
      <div
        className="bg-muted mt-2 flex h-2.5 w-full overflow-hidden rounded-full"
        role="img"
        aria-label={`Your code ${ms(timing.think_ms, 0)}, server ${ms(timing.server_ms, 0)}, other ${ms(other, 0)}`}
      >
        <span className={THINK} style={{ width: `${share(timing.think_ms)}%` }} />
        <span className={SERVER} style={{ width: `${share(timing.server_ms)}%` }} />
        <span className={OTHER} style={{ width: `${share(other ?? 0)}%` }} />
      </div>
      <dl className="mt-2.5 space-y-1 text-xs">
        <Row swatch={THINK} label="Your code" value={timing.think_ms} pct={pct(timing.think_ms)} />
        <Row swatch={SERVER} label="Server" value={timing.server_ms} pct={pct(timing.server_ms)} />
        <Row swatch={OTHER} label="Network & waiting" value={other} pct={pct(other)} />
      </dl>

      <div className="my-3 border-t" />

      <div className="flex items-baseline justify-between">
        <span className="flex items-center gap-1 font-medium">
          Sim time
          <Hint>
            How long the robot moved: one tick is 10 ms of simulated time, so this is your
            step count in seconds. Independent of hardware and network — and the only clock
            that ranks, because it is nav steps in other units.
          </Hint>
        </span>
        <span className="font-mono">{formatSeconds(timing.sim_ms / 1000)}</span>
      </div>

      <div className="my-3 border-t" />

      <p className="flex items-center gap-1 text-xs font-medium">
        Your code, per step
        <Hint>
          Measured by the server from the gap between your SDK acknowledging a frame and
          sending the next action, so the network cancels out.{" "}
          {timing.steps_measured.toLocaleString()} steps measured.
        </Hint>
      </p>
      <dl className="mt-1.5 grid grid-cols-3 gap-2 font-mono text-xs">
        <Stat label="mean" value={ms(timing.think_mean_ms)} />
        <Stat label="p95" value={ms(timing.think_p95_ms)} />
        <Stat label="max" value={ms(timing.think_max_ms)} />
      </dl>
      <dl className="text-muted-foreground mt-2 flex items-baseline justify-between text-xs">
        <dt className="flex items-center gap-1">
          Reported by your SDK
          <Hint>
            What your own process timed around your code. It should agree with the measured
            figure within a few milliseconds per step.
          </Hint>
        </dt>
        <dd className={cn("font-mono", reportedDiffers && "text-amber-600")}>
          {ms(timing.reported_mean_ms)} mean
        </dd>
      </dl>

      <p className="text-muted-foreground mt-3 text-[11px] leading-snug">
        Time never ranks; it is here so you can see where yours went. SPL — your steps against the shortest route's — does.
      </p>
    </div>
  );
}

function Row({
  swatch,
  label,
  value,
  pct,
}: {
  swatch: string;
  label: string;
  value: number | null;
  pct: number | null;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-muted-foreground flex items-center gap-1.5">
        <span className={cn("size-2 shrink-0 rounded-full", swatch)} aria-hidden />
        {label}
      </dt>
      <dd className="font-mono">
        {value === null ? "—" : formatSeconds(value / 1000)}
        <span className="text-muted-foreground">
          {pct === null ? "" : ` · ${pct}%`}
        </span>
      </dd>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button type="button" aria-label="More" className="text-muted-foreground">
            <Info className="size-3.5" aria-hidden />
          </button>
        }
      />
      <TooltipContent className="max-w-64">{children}</TooltipContent>
    </Tooltip>
  );
}
